// src/pages/Users/CreateUserModal.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import userService from '../../services/userService';
import { toast } from 'react-toastify';

const CreateUserModal = ({ show, onClose, onSubmit, role, title }) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    password: '',
    fullName: '',
    phone: '',
    email: '',
    commissionRate: 0,
    commissionType: 'net',
    walletBalance: 0,
    parentId: ''
  });
  const [loading, setLoading] = useState(false);
  const [availableParents, setAvailableParents] = useState([]);
  const [parentRole, setParentRole] = useState('');
  const [requiresParent, setRequiresParent] = useState(false);
  const [generatedUsername, setGeneratedUsername] = useState('');
  const [selectedParentDetails, setSelectedParentDetails] = useState(null);
  const [autoCommissionType, setAutoCommissionType] = useState(null);
  const [maxCommissionRate, setMaxCommissionRate] = useState(100);
  
  // Check if creating a regular user
  const isUserRole = role === 'user';
  
  // Only admin and sub-admin can manually select commission type
  // Distributor, retailer, user will have auto-selected (disabled) commission type
  const canSelectCommissionType = role === 'admin' || role === 'sub-admin';
  const autoSelectCommissionType = role === 'distributor' || role === 'retailer' || role === 'user';
  
  // Check if role requires commission rate (all except user)
  const requiresCommissionRate = !isUserRole;

  useEffect(() => {
    if (show) {
      // Reset form when modal opens
      setFormData({
        password: '',
        fullName: '',
        phone: '',
        email: '',
        commissionRate: 0,
        commissionType: 'net',
        walletBalance: 0,
        parentId: ''
      });
      setGeneratedUsername('');
      setSelectedParentDetails(null);
      setAutoCommissionType(null);
      setMaxCommissionRate(100);
      fetchAvailableParents();
    }
  }, [show, role]);

  const fetchAvailableParents = async () => {
    try {
      let parentRoleName = '';
      let requiresParentSelection = false;
      
      switch(role) {
        case 'admin':
          requiresParentSelection = false;
          parentRoleName = '';
          break;
        case 'sub-admin':
          requiresParentSelection = true;
          parentRoleName = 'admin';
          break;
        case 'distributor':
          requiresParentSelection = true;
          parentRoleName = 'sub-admin';
          break;
        case 'retailer':
          requiresParentSelection = true;
          parentRoleName = 'distributor';
          break;
        case 'user':
          requiresParentSelection = true;
          parentRoleName = 'retailer';
          break;
        default:
          requiresParentSelection = false;
      }
      
      setRequiresParent(requiresParentSelection);
      setParentRole(parentRoleName);
      
      if (requiresParentSelection && parentRoleName) {
        const response = await userService.getUsersByRole(parentRoleName, 1, 100);
        setAvailableParents(response.users || []);
      }
    } catch (error) {
      console.error('Error fetching parents:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'commissionRate') {
      let numericValue = value === '' ? 0 : parseFloat(value);
      if (isNaN(numericValue)) numericValue = 0;
      numericValue = Math.round(numericValue * 100) / 100;
      
      // Validate against parent's commission rate if parent is selected
      if (selectedParentDetails && selectedParentDetails.commissionRate !== undefined) {
        if (numericValue > selectedParentDetails.commissionRate) {
          toast.error(`Commission rate cannot exceed parent's commission rate of ${selectedParentDetails.commissionRate}%`);
          return;
        }
      }
      
      // Validate against max limit
      if (numericValue > maxCommissionRate) {
        toast.error(`Commission rate cannot exceed ${maxCommissionRate}%`);
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } 
    else if (name === 'walletBalance') {
      if (value === '' || value === null || value === undefined) {
        setFormData(prev => ({ ...prev, [name]: 0 }));
        return;
      }
      
      let intValue = parseInt(value, 10);
      
      if (isNaN(intValue)) {
        intValue = 0;
      }
      
      if (intValue < 0) intValue = 0;
      
      setFormData(prev => ({
        ...prev,
        [name]: intValue
      }));
    } 
    else if (name === 'parentId') {
      // Find selected parent details
      const selectedParent = availableParents.find(parent => parent._id === value);
      setSelectedParentDetails(selectedParent || null);
      
      // Update max commission rate based on parent's commission rate
      if (selectedParent && selectedParent.commissionRate !== undefined) {
        const parentRate = selectedParent.commissionRate;
        setMaxCommissionRate(parentRate);
        
        // Validate current commission rate against new parent rate
        if (formData.commissionRate > parentRate) {
          toast.warning(`Commission rate adjusted from ${formData.commissionRate}% to maximum allowed ${parentRate}%`);
          setFormData(prev => ({
            ...prev,
            commissionRate: parentRate,
            [name]: value
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            [name]: value
          }));
        }
      } else {
        setMaxCommissionRate(100);
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
      
      // Auto-set commission type for distributor, retailer, user based on parent
      if (autoSelectCommissionType && selectedParent && selectedParent.commissionType) {
        const inheritedType = selectedParent.commissionType;
        setAutoCommissionType(inheritedType);
        setFormData(prev => ({
          ...prev,
          commissionType: inheritedType
        }));
      } else if (autoSelectCommissionType && selectedParent && !selectedParent.commissionType) {
        setAutoCommissionType(null);
        setFormData(prev => ({
          ...prev,
          commissionType: 'net'
        }));
      }
    }
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.password) {
      toast.error('Password is required');
      return;
    }
    
    // Validate commission type for admin and sub-admin only
    if (canSelectCommissionType && !formData.commissionType) {
      toast.error('Please select a commission type');
      return;
    }
    
    // Validate commission rate for all non-user roles
    if (requiresCommissionRate) {
      if (formData.commissionRate < 0) {
        toast.error('Commission rate cannot be negative');
        return;
      }
      
      // Check against parent's commission rate
      if (selectedParentDetails && selectedParentDetails.commissionRate !== undefined) {
        if (formData.commissionRate > selectedParentDetails.commissionRate) {
          toast.error(`Commission rate cannot exceed parent's commission rate of ${selectedParentDetails.commissionRate}%`);
          return;
        }
      }
      
      // Check against max limit
      if (formData.commissionRate > maxCommissionRate) {
        toast.error(`Commission rate cannot exceed ${maxCommissionRate}%`);
        return;
      }
    }
    
    // For user role
    if (isUserRole) {
      if (!formData.parentId) {
        toast.error('Please select a retailer');
        return;
      }
      
      let finalBalance = parseInt(formData.walletBalance) || 0;
      
      const userData = {
        password: formData.password,
        walletBalance: finalBalance,
        parentId: formData.parentId,
        role: 'user'
      };
      
      console.log('Sending user data:', userData);
      
      setLoading(true);
      try {
        const response = await onSubmit(userData);
        if (response?.generatedUsername) {
          setGeneratedUsername(response.generatedUsername);
          toast.success(`User created successfully! Username: ${response.generatedUsername}`);
        }
        setFormData({
          password: '',
          fullName: '',
          phone: '',
          email: '',
          commissionRate: 0,
          commissionType: 'net',
          walletBalance: 0,
          parentId: ''
        });
        onClose();
      } catch (error) {
        // Error handled in parent
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // For admin/sub-admin/distributor/retailer roles
    if (requiresParent && !formData.parentId) {
      toast.error(`Please select a ${parentRole}`);
      return;
    }
    
    setLoading(true);
    try {
      const submitData = {
        password: formData.password,
        walletBalance: parseInt(formData.walletBalance) || 0,
        commissionRate: formData.commissionRate,
        role: role,
        parentId: formData.parentId || null
      };
      
      // Only include commissionType for admin and sub-admin
      if (canSelectCommissionType) {
        submitData.commissionType = formData.commissionType;
      }
      
      // Include optional fields if they have values
      if (formData.fullName) submitData.fullName = formData.fullName;
      if (formData.phone) submitData.phone = formData.phone;
      if (formData.email) submitData.email = formData.email;
      
      console.log('Sending submit data:', submitData);
      
      const response = await onSubmit(submitData);
      if (response?.generatedUsername) {
        setGeneratedUsername(response.generatedUsername);
        toast.success(`${title} created successfully! Username: ${response.generatedUsername}`);
      }
      setFormData({
        password: '',
        fullName: '',
        phone: '',
        email: '',
        commissionRate: 0,
        commissionType: 'net',
        walletBalance: 0,
        parentId: ''
      });
      onClose();
    } catch (error) {
      // Error handled in parent
      console.error('Submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getParentLabel = () => {
    switch(role) {
      case 'sub-admin':
        return 'Select Admin (Parent)';
      case 'distributor':
        return 'Select Sub-Admin (Parent)';
      case 'retailer':
        return 'Select Distributor (Parent)';
      case 'user':
        return 'Select Retailer';
      default:
        return 'Parent User';
    }
  };

  const getCommissionTypeHelpText = () => {
    return (
      <small className="text-muted d-block mt-1">
        <strong>Net:</strong> Commission calculated on net profit<br />
        <strong>Sales:</strong> Commission calculated on total sales amount
      </small>
    );
  };

  const getCommissionRateHelpText = () => {
    if (selectedParentDetails && selectedParentDetails.commissionRate !== undefined) {
      return (
        <small className="text-info d-block mt-1">
          <i className="bi bi-info-circle me-1"></i>
          Maximum allowed commission rate: <strong>{selectedParentDetails.commissionRate}%</strong> 
          (Parent {parentRole}'s commission rate)
          <br />
          Downline commission will be deducted from parent's {selectedParentDetails.commissionRate}%
        </small>
      );
    }
    return (
      <small className="text-muted d-block mt-1">
        Commission rate cannot exceed parent's commission rate
      </small>
    );
  };

  if (!show) return null;

  return (
    <div className="modal fade admin-modal-one show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h1 className="modal-title">Add New {title}</h1>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row g-3">
                {generatedUsername && (
                  <div className="col-12">
                    <div className="alert alert-success">
                      <strong>Username Generated:</strong> {generatedUsername}
                    </div>
                  </div>
                )}
                
                <div className="col-12">
                  <label className="modal-label">Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="form-control modal-input"
                    required
                  />
                  
                </div>
                
                {!isUserRole && (
                  <>
                    <div className="col-12">
                      <label className="modal-label">Full Name (Optional)</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className="form-control modal-input"
                        placeholder="Enter full name"
                      />
                    </div>
                    
                    <div className="col-12">
                      <label className="modal-label">
                        Commission Rate (%) 
                        <span className="text-danger ms-1">*</span>
                        {selectedParentDetails && selectedParentDetails.commissionRate !== undefined && (
                          <span className="badge bg-info ms-2">
                            Max: {selectedParentDetails.commissionRate}%
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        name="commissionRate"
                        value={formData.commissionRate}
                        onChange={handleChange}
                        className="form-control modal-input"
                        step="0.01"
                        min="0"
                        max={maxCommissionRate}
                        required
                      />
                    </div>
                  </>
                )}
                
                {/* Commission Type Field - Always visible */}
                <div className="col-12">
                  <label className="modal-label">
                    Commission Type 
                    {autoSelectCommissionType && <span className="text-muted ms-2">(Auto-selected from parent)</span>}
                    {canSelectCommissionType && <span className="text-danger ms-1">*</span>}
                  </label>
                  <select
                    name="commissionType"
                    value={formData.commissionType}
                    onChange={handleChange}
                    className="form-select"
                    disabled={autoSelectCommissionType}
                    required={canSelectCommissionType}
                  >
                    <option value="net">Net Profit Commission</option>
                    <option value="sales">Sales Commission</option>
                  </select>
                  {canSelectCommissionType}
                  {autoSelectCommissionType && selectedParentDetails && selectedParentDetails.commissionType && (
                    <small className="text-info d-block mt-1">
                      <i className="bi bi-info-circle me-1"></i>
                      Inherited from {parentRole}: {selectedParentDetails.username} ({selectedParentDetails.commissionType.toUpperCase()})
                    </small>
                  )}
                  {autoSelectCommissionType && !selectedParentDetails && (
                    <small className="text-warning d-block mt-1">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Select a {parentRole} to auto-set commission type
                    </small>
                  )}
                </div>
                
                <div className="col-12">
                  <label className="modal-label">
                    Initial Balance (₹) {isUserRole && '(Optional)'}
                  </label>
                  <input
                    type="number"
                    name="walletBalance"
                    value={formData.walletBalance}
                    onChange={handleChange}
                    className="form-control modal-input"
                    step="1"
                    min="0"
                    placeholder="0"
                    onKeyDown={(e) => {
                      if (e.key === '.' || e.key === ',') {
                        e.preventDefault();
                      }
                    }}
                  />
                  <small className="text-muted">
                    Amount will be deducted from your wallet
                  </small>
                </div>
                
                {requiresParent && (
                  <div className="col-12">
                    <label className="modal-label">{getParentLabel()} *</label>
                    <select
                      name="parentId"
                      value={formData.parentId}
                      onChange={handleChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select {parentRole}</option>
                      {availableParents.length > 0 ? (
                        availableParents.map(parent => (
                          <option key={parent._id} value={parent._id}>
                            {parent.username} - {parent.fullName || parent.username}
                            {parent.role && ` (${parent.role})`}
                            {parent.commissionRate !== undefined && ` | Rate: ${parent.commissionRate}%`}
                            {parent.commissionType && ` | Type: ${parent.commissionType.toUpperCase()}`}
                            {parent.walletBalance !== undefined && ` | Balance: ₹${Math.floor(parent.walletBalance)}`}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No {parentRole}s available</option>
                      )}
                    </select>
                    {availableParents.length === 0 && (
                      <small className="text-danger d-block mt-1">
                        No {parentRole}s available. Please create a {parentRole} first.
                      </small>
                    )}
                   
                  </div>
                )}
                
                <div className="col-12">
                  <button 
                    type="submit" 
                    className="addnew-user" 
                    disabled={loading || (requiresParent && availableParents.length === 0)}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Creating...
                      </>
                    ) : (
                      `Add ${title}`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal;